import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Settings2, Trash2, RotateCcw, BadgeCheck, CircleDashed, Code2 } from "lucide-react";
import { CloudIcon, LangIcon } from "@/components/brand-icons";
import { cn } from "@/lib/utils";

export function VerifiedIcon({ verified, className }) {
  if (!verified?.checked) {
    return <CircleDashed className={cn("h-4 w-4 text-slate-300", className)} title="Verification pending — upstream registry unreachable" data-testid="verified-unknown" />;
  }
  if (verified.any) {
    const sources = [verified.terraform && "Terraform Registry", verified.bicep && "Azure Bicep types"].filter(Boolean).join(" · ");
    return <BadgeCheck className={cn("h-4 w-4 text-sky-600", className)} title={`Verified · official release (${sources})`} data-testid="verified-icon" />;
  }
  return null;
}

function TypeBadge({ language, label, verified, title }) {
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-sm border px-2 py-0.5", verified ? "border-sky-200 bg-sky-50 text-sky-800" : "border-slate-200 bg-slate-50")} title={title}>
      <LangIcon language={language} className="h-3 w-3" /> {label}
    </span>
  );
}

function ResourceBadges({ r }) {
  return (
    <div className="mt-4 flex flex-wrap gap-2 font-mono-tight text-[11px] text-slate-500">
      {r.short_name && (
        <span className="rounded-sm border border-slate-900 bg-slate-900 px-2 py-0.5 text-white" data-testid={`short-${r.key}`}>short · {r.short_name}</span>
      )}
      {r.terraform_type && (
        <TypeBadge language="terraform" label={r.terraform_type} verified={r.verified?.terraform} title={r.verified?.terraform ? "Official Terraform provider resource" : "Not found in the official provider docs"} />
      )}
      {r.bicep_type && (
        <TypeBadge language="bicep" label={r.bicep_type.split("/").pop()} verified={r.verified?.bicep} title={r.verified?.bicep ? `Official Azure resource type · API ${r.verified.bicep_api_version}` : "Not found in the official Bicep types"} />
      )}
      {r.custom && <span className="rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700">custom</span>}
      {(r.terraform_code || r.bicep_code) && (
        <span className="inline-flex items-center gap-1 rounded-sm border border-violet-200 bg-violet-50 px-2 py-0.5 text-violet-700" data-testid={`template-${r.key}`}><Code2 className="h-3 w-3" /> template</span>
      )}
    </div>
  );
}

function ResourceDefaults({ r }) {
  const entries = Object.entries(r.defaults || {});
  if (!entries.length) return null;
  return (
    <div className="mt-4 rounded-sm border border-slate-100 bg-slate-50/70 p-3" data-testid={`defaults-${r.key}`}>
      <p className="mb-2 font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">// defaults</p>
      <div className="space-y-1 font-mono-tight text-xs">
        {entries.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4">
            <span className="text-slate-500">{k}</span>
            <span className="text-slate-800">{String(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ResourceActions({ r, onConfigure, onDelete, onRestore }) {
  if (r.disabled) {
    return (
      <Button variant="ghost" size="sm" onClick={() => onRestore(r)} data-testid={`restore-${r.key}`}>
        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore
      </Button>
    );
  }
  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => onConfigure(r)} data-testid={`edit-defaults-${r.key}`}>
        <Settings2 className="mr-1 h-3.5 w-3.5" /> Configure
      </Button>
      <Button variant="ghost" size="sm" onClick={() => onDelete(r)} data-testid={`delete-${r.key}`}>
        <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
      </Button>
    </>
  );
}

export function ResourceCard({ r, isAdmin, onConfigure, onDelete, onRestore }) {
  const verified = r.verified?.checked && r.verified?.any;
  return (
    <Card
      className={cn("rounded-md border bg-white p-5 shadow-none transition-all", r.disabled ? "border-slate-200 opacity-60" : "border-slate-200 hover:-translate-y-px hover:shadow-sm", verified && !r.disabled && "border-sky-100")}
      data-testid={`resource-${r.key}`}
      data-verified={verified ? "true" : "false"}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-base font-semibold text-slate-900">{r.name}</h3>
            <VerifiedIcon verified={r.verified} />
            {r.disabled && <span className="font-mono-tight text-[10px] uppercase tracking-widest text-rose-600">deleted</span>}
          </div>
          <p className="mt-1 text-sm text-slate-600">{r.description}</p>
        </div>
        <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 font-mono-tight text-[10px] uppercase tracking-widest text-slate-600">{r.category}</span>
      </div>
      <ResourceBadges r={r} />
      <ResourceDefaults r={r} />
      <div className="mt-4 flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
        <span className="inline-flex items-center gap-1.5 font-mono-tight text-[10px] uppercase tracking-widest text-slate-400" data-testid={`cloud-${r.key}`} title={r.cloud}>
          <CloudIcon cloud={r.cloud} className="h-3.5 w-3.5" /> {r.cloud}
        </span>
        <div className="flex items-center justify-end gap-1">
          {isAdmin && <ResourceActions r={r} onConfigure={onConfigure} onDelete={onDelete} onRestore={onRestore} />}
        </div>
      </div>
    </Card>
  );
}
